// ================================================================================
// ProjectSectionViewer — composant COMMUN du dossier de production.
// Responsable, pour toutes les rubriques documentaires (Concept, Proposition ACA,
// Modèle économique) et de manière homogène côté admin comme côté porteur, de :
//   · l'affichage et l'édition du contenu,
//   · du défilement : molette, trackpad, tactile, clavier (flèches, PageUp/Down,
//     Home/End), glissement direct du contenu et gros curseur de 22 px draggable,
//   · du plein écran avec grand bouton « ✕ QUITTER LE PLEIN ÉCRAN »,
//   · du brouillon / de la publication / des versions,
//   · des notifications e-mail (porteur et/ou super administrateur, cases
//     indépendantes — un e-mail part uniquement au clic d'une action réelle).
// La position de lecture est conservée (sessionStorage) à la réouverture.
// ================================================================================
import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { X, History, RotateCcw, FileEdit, Maximize2, Mail, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr } from "../../lib/api";

const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

// Glisser-défiler : permet de monter/descendre en glissant directement sur le contenu
// (souris ou doigt), sans viser la barre de défilement. La sélection de texte reste
// possible au clic simple ; dès qu'un glisser vertical est détecté, la sélection est bloquée.
export function useDragScroll(externalRef) {
  const internalRef = useRef(null);
  const ref = externalRef || internalRef;
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let startY = 0, startTop = 0, down = false, moved = false;
    const onDown = (e) => {
      if (e.pointerType === "mouse" && e.button !== 0) return;
      down = true; moved = false; startY = e.clientY; startTop = el.scrollTop;
    };
    const onMove = (e) => {
      if (!down) return;
      const dy = e.clientY - startY;
      if (!moved && Math.abs(dy) > 6) {
        moved = true;
        el.classList.add("aca-dragging");
        window.getSelection()?.removeAllRanges();
      }
      if (moved) {
        el.scrollTop = startTop - dy;
        window.getSelection()?.removeAllRanges();
        e.preventDefault();
      }
    };
    const onSelect = (e) => { if (moved) e.preventDefault(); };
    const onUp = () => {
      if (moved) window.getSelection()?.removeAllRanges();
      down = false; moved = false; el.classList.remove("aca-dragging");
    };
    el.addEventListener("pointerdown", onDown);
    el.addEventListener("selectstart", onSelect);
    window.addEventListener("pointermove", onMove, { passive: false });
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      el.removeEventListener("pointerdown", onDown);
      el.removeEventListener("selectstart", onSelect);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [ref]);
  return ref;
}

// Défilement à la molette / trackpad : appliqué manuellement pour un défilement
// fluide et garanti identique partout (gère les deltas en pixels, lignes ou pages).
// Attaché en natif avec { passive: false } via attachWheelScroll, afin que
// preventDefault fonctionne sans avertissement de la console.
function wheelStep(el, e) {
  if (!el || el.scrollHeight <= el.clientHeight + 1) return; // rien à défiler : on laisse la page
  const unit = e.deltaMode === 1 ? 18 : e.deltaMode === 2 ? el.clientHeight : 1;
  const delta = e.deltaY * unit;
  // Panneau arrivé en haut ou en bas : on NE capture PLUS la molette — la page
  // (ou l'espace de travail plein écran) continue de défiler normalement.
  const atTop = el.scrollTop <= 0;
  const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
  if ((delta < 0 && atTop) || (delta > 0 && atBottom)) return;
  el.scrollTop += delta;
  e.preventDefault();
}
export function attachWheelScroll(el) {
  if (!el || el.__acaWheel) return;
  el.__acaWheel = true;
  el.addEventListener("wheel", (e) => wheelStep(el, e), { passive: false });
}

// ---------- Zone de défilement commune ----------
// Barre verticale de 22 px toujours visible (piste contrastée, gros curseur),
// molette / trackpad / tactile natifs, clavier, glisser-défiler, position conservée.
export function SectionScroll({ persistKey, maxHeight = "420px", className = "", testid, children }) {
  const ref = useRef(null);
  useDragScroll(ref);
  useEffect(() => { attachWheelScroll(ref.current); }, []);

  // Restaure la position de lecture à l'ouverture de la rubrique.
  useEffect(() => {
    const el = ref.current;
    if (!el || !persistKey) return;
    const saved = Number(sessionStorage.getItem(`aca-scroll-${persistKey}`) || 0);
    if (saved > 0) el.scrollTop = saved;
  }, [persistKey]);

  const onScroll = () => {
    if (persistKey && ref.current) sessionStorage.setItem(`aca-scroll-${persistKey}`, String(ref.current.scrollTop));
  };

  const onKeyDown = (e) => {
    const el = ref.current;
    if (!el) return;
    const line = 56;
    const page = Math.max(120, el.clientHeight * 0.9);
    if (e.key === "ArrowDown") { el.scrollBy({ top: line, behavior: "smooth" }); e.preventDefault(); }
    else if (e.key === "ArrowUp") { el.scrollBy({ top: -line, behavior: "smooth" }); e.preventDefault(); }
    else if (e.key === "PageDown") { el.scrollBy({ top: page, behavior: "smooth" }); e.preventDefault(); }
    else if (e.key === "PageUp") { el.scrollBy({ top: -page, behavior: "smooth" }); e.preventDefault(); }
    else if (e.key === "Home") { el.scrollTo({ top: 0, behavior: "smooth" }); e.preventDefault(); }
    else if (e.key === "End") { el.scrollTo({ top: el.scrollHeight, behavior: "smooth" }); e.preventDefault(); }
  };

  return (
    <div ref={ref} onScroll={onScroll} onKeyDown={onKeyDown} tabIndex={0}
      role="region" aria-label="Zone défilante — molette, tactile, flèches du clavier ou curseur"
      data-testid={testid} style={{ maxHeight }}
      className={`aca-section-scroll aca-dragscroll ${className}`}>
      {children}
    </div>
  );
}

// ---------- Plein écran commun ----------
// En-tête fixe + grand bouton de sortie bien visible ; le contenu défile librement.
export function FullscreenViewer({ title, subtitle, onClose, testid, children }) {
  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-void aca-modal" data-testid={testid}>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 bg-surface px-4 py-3 sm:px-6">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-cyan">{subtitle}</div>
          <div className="truncate font-display text-base font-700 text-white">{title}</div>
        </div>
        <button onClick={onClose} data-testid={`${testid}-close`}
          className="flex flex-shrink-0 items-center gap-2 rounded-full border-2 border-red-400/70 bg-red-500/20 px-5 py-2.5 text-sm font-700 uppercase tracking-wider text-red-200 shadow-[0_0_16px_rgba(244,63,94,0.35)] transition-colors hover:bg-red-500/40 hover:text-white">
          ✕ Quitter le plein écran
        </button>
      </div>
      <div className="aca-dossier-paper flex min-h-0 flex-1 flex-col" style={{ borderRadius: 0 }}>{children}</div>
    </div>
  );
}

// ---------- Demande de modification (porteur) ----------
export function RequestModal({ pid, apiBase, sectionKey, sectionLabel, onClose, onSent }) {
  const [f, setF] = useState({ title: "", description: "", proposal: "", reason: "" });
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const send = async () => {
    if (!f.title.trim()) return toast.error("Indiquez le titre de votre demande.");
    setSending(true);
    try {
      let fileMeta = null;
      if (file) {
        const fd = new FormData(); fd.append("file", file); fd.append("context", "demande");
        fileMeta = (await api.post(`${apiBase}/files`, fd)).data;
      }
      await api.post(`${apiBase}/requests`, {
        section_key: sectionKey, ...f,
        file_id: fileMeta?.id, file_name: fileMeta?.name,
      });
      toast.success("Demande envoyée à APP CHALLENGE ACADEMY — vous serez notifié de la réponse.");
      onSent();
    } catch (e) { toast.error(apiErr(e)); setSending(false); }
  };

  const Field = ({ k, label, rows = 2, testid }) => (
    <label className="block">
      <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">{label}</span>
      <textarea value={f[k]} onChange={set(k)} rows={rows} data-testid={testid}
        className="w-full resize-y rounded-xl border border-white/10 bg-surface px-4 py-2.5 text-sm text-white outline-none focus:border-cyan focus:ring-1 focus:ring-cyan" />
    </label>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto overscroll-contain bg-black/70 p-4 aca-modal" data-testid="request-modal">
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
        className="aca-section-scroll mt-10 max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-2xl border border-white/10 bg-elevated p-6 sm:p-8">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-700 text-white">Proposer une modification</h3>
          <button onClick={onClose} data-testid="request-close" className="text-ink-secondary hover:text-white"><X size={20} /></button>
        </div>
        <p className="mb-4 rounded-xl border border-cyan/25 bg-cyan/5 p-3 text-xs leading-relaxed text-ink-secondary">
          Section concernée : <span className="font-600 text-cyan">{sectionLabel}</span>. Votre proposition ne remplace
          pas la version officielle : ACA l'étudie et peut l'accepter, l'adapter ou la refuser. Rien n'est perdu.
        </p>
        <div className="space-y-3">
          <label className="block">
            <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">Titre de la demande</span>
            <input value={f.title} onChange={set("title")} data-testid="request-title"
              className="w-full rounded-xl border border-white/10 bg-surface px-4 py-2.5 text-sm text-white outline-none focus:border-cyan focus:ring-1 focus:ring-cyan" />
          </label>
          <Field k="description" label="Description" testid="request-description" />
          <Field k="proposal" label="Modification proposée" testid="request-proposal" />
          <Field k="reason" label="Pourquoi cette modification ?" rows={2} testid="request-reason" />
          <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-dashed border-white/20 px-4 py-3 text-xs text-ink-secondary transition-colors hover:border-cyan/50" data-testid="request-file">
            <Paperclip size={14} className="text-cyan" />
            {file ? file.name : "Joindre un fichier (image, PDF, document… max 10 Mo)"}
            <input type="file" className="hidden" accept=".jpg,.jpeg,.png,.webp,.pdf,.doc,.docx,.xls,.xlsx,.zip"
              onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </label>
        </div>
        <div className="mt-5 flex gap-3">
          <button onClick={send} disabled={sending} data-testid="request-send" className="aca-btn-primary flex-1">
            {sending ? "Envoi…" : "Envoyer à APP CHALLENGE ACADEMY"}
          </button>
          <button onClick={onClose} data-testid="request-cancel" className="aca-btn-secondary flex-1">Annuler</button>
        </div>
      </motion.div>
    </div>
  );
}

// ---------- Section documentaire : Concept / Proposition ACA / Modèle économique ----------
// Admin : brouillon, publication, versions, notifications. Porteur : lecture de la
// version officielle + proposition de modification. Même défilement des deux côtés.
export default function ProjectSectionViewer({ pid, apiBase, mode, sec, onChanged }) {
  const [draft, setDraft] = useState(sec.draft ?? sec.published ?? "");
  const [saving, setSaving] = useState("");
  const [showVersions, setShowVersions] = useState(false);
  const [openVersion, setOpenVersion] = useState(null);
  const [notifyEmail, setNotifyEmail] = useState(false);
  const [notifyAdmin, setNotifyAdmin] = useState(false);
  const [reqOpen, setReqOpen] = useState(false);
  const [fullOpen, setFullOpen] = useState(false);
  const isAdmin = mode === "admin";
  useEffect(() => { setDraft(sec.draft ?? sec.published ?? ""); }, [sec.draft, sec.published]);

  const saveDraft = async () => {
    setSaving("draft");
    try { await api.put(`${apiBase}/sections/${sec.key}`, { draft }); toast.success("Brouillon enregistré (invisible du porteur tant qu'il n'est pas publié)."); onChanged(); }
    catch (e) { toast.error(apiErr(e)); } finally { setSaving(""); }
  };
  const publish = async () => {
    setSaving("publish");
    try {
      await api.post(`${apiBase}/sections/${sec.key}/publish`, { text: draft, notify_email: notifyEmail, notify_admin: notifyAdmin });
      toast.success("Nouvelle version publiée — visible par le porteur de projet."); onChanged();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(""); }
  };
  const restore = async (vid) => {
    try { await api.post(`${apiBase}/sections/${sec.key}/versions/${vid}/restore`); toast.success("Version restaurée en brouillon — publiez-la pour la rendre officielle."); onChanged(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const SEC_STATE = {
    brouillon: { l: "Brouillon", c: "text-amber-300 bg-amber-400/15" },
    publie: { l: "Publié", c: "text-green-400 bg-green-500/15" },
  };

  // Zones de lecture / d'édition : normales et plein écran, même défilement.
  const reader = (maxHeight, tid) => (
    <SectionScroll persistKey={`${pid}-${sec.key}`} maxHeight={maxHeight} testid={tid} className="rounded-xl border border-white/10 bg-surface p-5 pr-1">
      <p className="whitespace-pre-line pr-4 text-sm leading-relaxed text-ink-secondary" data-testid={`doc-published-${sec.key}`}>{sec.published}</p>
      <p className="mt-3 border-t border-white/5 pt-2 pr-4 text-[10px] text-ink-tertiary">
        Astuce : molette, trackpad, doigt, flèches du clavier, glisser direct sur le texte ou gros curseur à droite.
      </p>
    </SectionScroll>
  );
  const editor = (cls, tid) => (
    <textarea ref={attachWheelScroll} value={draft} onChange={(e) => setDraft(e.target.value)} data-testid={tid || `doc-draft-${sec.key}`}
      placeholder={`Rédigez ici « ${sec.label} »…\n\nExemples : présentation, objectifs, fonctionnement, utilisateurs, parcours, fonctionnalités, avantages, évolution PWA / iOS / Android…`}
      className={`aca-section-scroll w-full resize-y rounded-xl border border-white/10 bg-surface px-4 py-3 text-sm leading-relaxed text-white placeholder:text-ink-tertiary outline-none focus:border-cyan focus:ring-1 focus:ring-cyan ${cls}`} />
  );

  return (
    <div className="space-y-4" data-testid={`dossier-doc-${sec.key}`}>
      <div className="flex flex-wrap items-center gap-2">
        {isAdmin && (
          <span className={`rounded-full px-2.5 py-1 text-[10px] font-700 uppercase ${SEC_STATE[sec.state]?.c || SEC_STATE.brouillon.c}`} data-testid={`doc-state-${sec.key}`}>
            {SEC_STATE[sec.state]?.l || "Brouillon"}
          </span>
        )}
        {!isAdmin && sec.published && (
          <span className="rounded-full bg-green-500/15 px-2.5 py-1 text-[10px] font-700 uppercase text-green-400" data-testid={`doc-state-${sec.key}`}>
            Version officielle ACA
          </span>
        )}
        {sec.published_at && <span className="text-[11px] text-ink-tertiary">Publié le {fmtDT(sec.published_at)} · {sec.versions.length} version(s)</span>}
        {sec.versions.length > 0 && (
          <button onClick={() => setShowVersions((v) => !v)} data-testid={`doc-versions-${sec.key}`}
            className="aca-btn-ghost border border-white/10"><History size={13} /> Versions ({sec.versions.length})</button>
        )}
        <button onClick={() => setFullOpen(true)} data-testid={`doc-fullscreen-${sec.key}`}
          className="aca-btn-ghost border border-white/10" title="Ouvrir le document sur toute la page">
          <Maximize2 size={13} /> Plein écran
        </button>
        {!isAdmin && (
          <button onClick={() => setReqOpen(true)} data-testid={`doc-request-${sec.key}`} className="aca-btn-secondary">
            <FileEdit size={13} /> Proposer une modification
          </button>
        )}
      </div>

      {isAdmin ? (
        <div>
          {editor("max-h-[60vh] min-h-[320px]")}
          <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2.5">
            <button onClick={saveDraft} disabled={!!saving} data-testid={`doc-save-${sec.key}`} className="aca-btn-secondary">
              {saving === "draft" ? "Enregistrement…" : "Enregistrer le brouillon"}
            </button>
            <button onClick={publish} disabled={!!saving || !draft.trim()} data-testid={`doc-publish-${sec.key}`} className="aca-btn-primary">
              {saving === "publish" ? "Publication…" : "Publier la nouvelle version"}
            </button>
            <label className="flex cursor-pointer items-center gap-2 text-xs text-ink-secondary" data-testid={`doc-notify-${sec.key}`}>
              <input type="checkbox" checked={notifyEmail} onChange={(e) => setNotifyEmail(e.target.checked)} className="h-4 w-4 accent-cyan" />
              <Mail size={13} className="text-cyan" /> Notifier le porteur par e-mail
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-xs text-ink-secondary" data-testid={`doc-notify-admin-${sec.key}`}>
              <input type="checkbox" checked={notifyAdmin} onChange={(e) => setNotifyAdmin(e.target.checked)} className="h-4 w-4 accent-amber-400" />
              <Mail size={13} className="text-amber-300" /> Notifier le Super Administrateur par e-mail
            </label>
          </div>
        </div>
      ) : (
        sec.published
          ? reader("420px", `doc-reader-${sec.key}`)
          : <p className="rounded-xl border border-white/10 bg-surface p-5 text-sm text-ink-tertiary">APP CHALLENGE ACADEMY prépare cette section — elle apparaîtra ici dès sa publication.</p>
      )}

      {fullOpen && (
        <FullscreenViewer
          title={sec.label}
          subtitle={isAdmin ? "Édition plein écran" : "Lecture plein écran"}
          onClose={() => setFullOpen(false)}
          testid={`doc-full-${sec.key}`}>
          {isAdmin ? (
            <div className="mx-auto flex min-h-0 w-full max-w-4xl flex-1 flex-col p-5 sm:p-8">
              {editor("min-h-0 flex-1 resize-none", `doc-full-${sec.key}-editor`)}
            </div>
          ) : (
            <div className="mx-auto flex min-h-0 w-full max-w-4xl flex-1 flex-col p-5 sm:p-8">
              {reader("none", `doc-full-${sec.key}-reader`)}
            </div>
          )}
        </FullscreenViewer>
      )}

      {showVersions && (
        <div className="space-y-2 rounded-xl border border-white/10 bg-surface p-4" data-testid={`doc-version-list-${sec.key}`}>
          <div className="text-[11px] uppercase tracking-[0.15em] text-ink-tertiary">Historique des versions</div>
          {sec.versions.map((v) => (
            <div key={v.id} className="rounded-lg border border-white/10">
              <button onClick={() => setOpenVersion(openVersion === v.id ? null : v.id)}
                className="flex w-full items-center justify-between px-3 py-2.5 text-left text-xs" data-testid={`doc-version-${sec.key}-${v.version}`}>
                <span className="font-600 text-white">Version {v.version}</span>
                <span className="text-ink-tertiary">{fmtDT(v.at)} · {v.author}</span>
              </button>
              {openVersion === v.id && (
                <div className="border-t border-white/10 p-3">
                  <SectionScroll maxHeight="240px" testid={`doc-version-text-${sec.key}-${v.version}`} className="pr-1">
                    <p className="whitespace-pre-line pr-4 text-xs leading-relaxed text-ink-secondary">{v.text}</p>
                  </SectionScroll>
                  {isAdmin && (
                    <button onClick={() => restore(v.id)} data-testid={`doc-restore-${sec.key}-${v.version}`} className="aca-btn-secondary mt-3">
                      <RotateCcw size={12} /> Restaurer cette version
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {reqOpen && (
        <RequestModal pid={pid} apiBase={apiBase} sectionKey={sec.key} sectionLabel={sec.label}
          onClose={() => setReqOpen(false)} onSent={() => { setReqOpen(false); onChanged(); }} />
      )}
    </div>
  );
}
